import { memo } from "react";
import { toast } from "react-toastify";
import { FormAdapter } from "hooks/useForm";
import { ResponseCode } from "consts/api";
import { ManagementApiResponseAdapter } from "util/api";
import { when } from "util/support";
import Centered from "ui/layouts/Centered";
import { PreApplication } from "ui/forms/ECommerce";
import When from "ui/support/When";
import usePreOnboarding from "./usePreOnboarding";
import PreApplicationCard from "./PreApplicationCard";
import RegistrationCompleteCta from "./RegistrationCompleteCta";

const PreOnboarding = () => {
    const {
        isRegistrationComplete,
        register,
        bootstrapData
    } = usePreOnboarding();

    const formProps: PreApplication.PreApplicationForm = {
        bootstrapData,
        initialValues: PreApplication.getInitialValues({}),
        isBootstrapDataLoading: false,
        onSaveOrCreate: async ({
            email,
            password,
            businessActivity,
            productService,
            productServiceOther
        }) => {
            try {
                const response = await register({
                    email: String(email),
                    password: String(password),
                    category: String(businessActivity?.id),
                    businessActivity: when(
                        productService,
                        () => productService!.id,
                        () => String(productServiceOther).trim()
                    )
                });

                return FormAdapter.fromResponse(response);
            }catch {
                const statusText = 'Something went wrong. Please try again later.';

                toast.error(statusText);

                return FormAdapter.fromResponse(
                    ManagementApiResponseAdapter.from(null, {
                        ok: false,
                        status: ResponseCode.BadRequest,
                        statusText
                    })
                );
            }
        }
    };

    const form = PreApplication.useForm(formProps);

    return (
        <Centered>
            <When
                if={isRegistrationComplete}
                then={() => (
                    <RegistrationCompleteCta
                        email={form.values[PreApplication.FormField.Email]}
                    />
                )}
            >
                <PreApplicationCard
                    slotProps={{
                        header: {
                            title: 'Create your account',
                            subheader: 'Please fill out the form below to create your account.'
                        }
                    }}
                >
                    <PreApplication.Form
                        {...formProps}
                        {...form}
                    />
                </PreApplicationCard>
            </When>
        </Centered>
    );
};

export default memo(PreOnboarding);